import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import type {
  EnrichedRouteStop,
  RouteDirections,
  RoutesUIState,
} from 'src/hooks/routes/types';

/**
 * Pending provider to be added to a route
 */
export interface PendingRouteProvider {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
}

/**
 * A saved route drawn on the Discovery map without opening the routes drawer
 * (Scout's "View Route on Map")
 */
export interface PreviewRoute {
  id: string;
  label: string;
}

/**
 * Extended state interface to include visibility and pending provider
 */
interface RoutesSliceState extends RoutesUIState {
  /** Map of route IDs to their visibility state on the map */
  routeVisibility: Record<string, boolean>;
  /** Provider pending to be added to a route (from profile page) */
  pendingProvider: PendingRouteProvider | null;
  /** Index of the leg currently hovered in the directions list (null = none) */
  hoveredLegIndex: number | null;
  /** Saved route previewed on the Discovery map (null = none) */
  previewRoute: PreviewRoute | null;
}

/**
 * Initial state for routes UI management
 */
const initialState: RoutesSliceState = {
  mode: 'idle',
  selectedRouteId: null,
  tempLabel: '',
  tempStops: [],
  isLoadingDirections: false,
  directions: null,
  directionsError: null,
  routeVisibility: {},
  pendingProvider: null,
  hoveredLegIndex: null,
  previewRoute: null,
};

/**
 * Redux slice for route creation/editing UI state
 *
 * This slice manages the temporary state during route creation and editing,
 * following the temp/applied pattern used elsewhere in the codebase.
 */
const routesSlice = createSlice({
  name: 'routes',
  initialState,
  reducers: {
    /**
     * Set the temporary label for the route
     */
    setTempLabel: (state, action: PayloadAction<string>) => {
      state.tempLabel = action.payload;
    },

    /**
     * Set all temporary stops at once (e.g., when loading existing route)
     */
    setTempStops: (state, action: PayloadAction<EnrichedRouteStop[]>) => {
      state.tempStops = action.payload;
    },

    /**
     * Add a stop to the temporary stops
     */
    addTempStop: (state, action: PayloadAction<EnrichedRouteStop>) => {
      // Add at the end with the next position
      const newStop = {
        ...action.payload,
        position: state.tempStops.length,
      };
      state.tempStops.push(newStop);
    },

    /**
     * Remove a stop from the temporary stops by ID
     */
    removeTempStop: (state, action: PayloadAction<string>) => {
      const stopId = action.payload;
      state.tempStops = state.tempStops
        .filter((stop) => stop.id !== stopId)
        .map((stop, index) => ({
          ...stop,
          position: index,
        }));
    },

    /**
     * Reorder the temporary stops
     */
    reorderTempStops: (state, action: PayloadAction<EnrichedRouteStop[]>) => {
      // Update positions based on new order
      state.tempStops = action.payload.map((stop, index) => ({
        ...stop,
        position: index,
      }));
    },

    /**
     * Update a specific stop in the temporary stops
     */
    updateTempStop: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<EnrichedRouteStop> }>
    ) => {
      const { id, updates } = action.payload;
      const index = state.tempStops.findIndex((stop) => stop.id === id);
      if (index !== -1) {
        state.tempStops[index] = {
          ...state.tempStops[index],
          ...updates,
        };
      }
    },

    /**
     * Set the loading state for directions
     */
    setIsLoadingDirections: (state, action: PayloadAction<boolean>) => {
      state.isLoadingDirections = action.payload;
    },

    /**
     * Set the directions result
     */
    setDirections: (state, action: PayloadAction<RouteDirections | null>) => {
      state.directions = action.payload;
      state.directionsError = null;
    },

    /**
     * Set the directions error
     */
    setDirectionsError: (state, action: PayloadAction<string | null>) => {
      state.directionsError = action.payload;
      state.directions = null;
    },

    /**
     * Clear directions data
     */
    clearDirections: (state) => {
      state.directions = null;
      state.directionsError = null;
      state.isLoadingDirections = false;
    },

    /**
     * Start creating a new route
     */
    startCreateMode: (state) => {
      state.mode = 'creating';
      state.previewRoute = null;
      state.selectedRouteId = null;
      state.tempLabel = '';
      state.tempStops = [];
      state.directions = null;
      state.directionsError = null;
    },

    /**
     * Start editing an existing route
     */
    startEditMode: (
      state,
      action: PayloadAction<{
        routeId: string;
        label: string;
        stops: EnrichedRouteStop[];
      }>
    ) => {
      const { routeId, label, stops } = action.payload;
      state.mode = 'editing';
      state.previewRoute = null;
      state.selectedRouteId = routeId;
      state.tempLabel = label;
      state.tempStops = stops;
      state.directions = null;
      state.directionsError = null;
    },

    /**
     * Start viewing a route (read-only)
     */
    startViewMode: (
      state,
      action: PayloadAction<{
        routeId: string;
        label: string;
        stops: EnrichedRouteStop[];
      }>
    ) => {
      const { routeId, label, stops } = action.payload;
      state.mode = 'viewing';
      state.previewRoute = null;
      state.selectedRouteId = routeId;
      state.tempLabel = label;
      state.tempStops = stops;
      state.directions = null;
      state.directionsError = null;
    },

    /**
     * Preview a saved route on the map, or pass null to clear it
     */
    setPreviewRoute: (state, action: PayloadAction<PreviewRoute | null>) => {
      state.previewRoute = action.payload;
    },

    /**
     * Cancel the current operation and reset to idle
     */
    cancelRouteEdit: (state) => {
      state.mode = 'idle';
      state.selectedRouteId = null;
      state.tempLabel = '';
      state.tempStops = [];
      state.directions = null;
      state.directionsError = null;
    },

    /**
     * Toggle visibility of a route on the map
     */
    toggleRouteVisibility: (state, action: PayloadAction<string>) => {
      const routeId = action.payload;
      state.routeVisibility[routeId] = !state.routeVisibility[routeId];
    },

    /**
     * Set visibility of a route on the map
     */
    setRouteVisibility: (
      state,
      action: PayloadAction<{ routeId: string; visible: boolean }>
    ) => {
      const { routeId, visible } = action.payload;
      state.routeVisibility[routeId] = visible;
    },

    /**
     * Set a pending provider to be added to a route
     */
    setPendingProvider: (
      state,
      action: PayloadAction<PendingRouteProvider>
    ) => {
      state.pendingProvider = action.payload;
    },

    /**
     * Clear the pending provider
     */
    clearPendingProvider: (state) => {
      state.pendingProvider = null;
    },

    /**
     * Set the hovered leg index for map highlighting
     */
    setHoveredLegIndex: (state, action: PayloadAction<number | null>) => {
      state.hoveredLegIndex = action.payload;
    },
  },
});

// Export actions
export const {
  setTempLabel,
  setTempStops,
  addTempStop,
  removeTempStop,
  reorderTempStops,
  updateTempStop,
  setIsLoadingDirections,
  setDirections,
  setDirectionsError,
  clearDirections,
  startCreateMode,
  startEditMode,
  startViewMode,
  cancelRouteEdit,
  toggleRouteVisibility,
  setRouteVisibility,
  setPendingProvider,
  clearPendingProvider,
  setHoveredLegIndex,
  setPreviewRoute,
} = routesSlice.actions;

// Selectors
export const selectRoutesMode = (state: RootState) => state.routes?.mode ?? 'idle';
export const selectSelectedRouteId = (state: RootState) =>
  state.routes?.selectedRouteId ?? null;
export const selectTempLabel = (state: RootState) => state.routes?.tempLabel ?? '';
export const selectTempStops = (state: RootState) => state.routes?.tempStops ?? [];
export const selectIsLoadingDirections = (state: RootState) =>
  state.routes?.isLoadingDirections ?? false;
export const selectDirections = (state: RootState) => state.routes?.directions ?? null;
export const selectDirectionsError = (state: RootState) =>
  state.routes?.directionsError ?? null;

// Derived selectors
export const selectIsCreating = (state: RootState) =>
  selectRoutesMode(state) === 'creating';
export const selectIsEditing = (state: RootState) =>
  selectRoutesMode(state) === 'editing';
export const selectIsViewing = (state: RootState) =>
  selectRoutesMode(state) === 'viewing';
export const selectCanSave = (state: RootState) => {
  const label = selectTempLabel(state);
  const stops = selectTempStops(state);
  return label.trim().length > 0 && stops.length >= 1;
};

export const selectRouteVisibility = (state: RootState) =>
  state.routes?.routeVisibility ?? {};

export const selectIsRouteVisible = (routeId: string) => (state: RootState) =>
  state.routes?.routeVisibility?.[routeId] ?? false;

export const selectPendingProvider = (state: RootState) =>
  state.routes?.pendingProvider ?? null;

export const selectPreviewRoute = (state: RootState) =>
  state.routes?.previewRoute ?? null;

export const selectHoveredLegIndex = (state: RootState) =>
  state.routes?.hoveredLegIndex ?? null;

// Export reducer
export default routesSlice.reducer;
